import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography } from 'antd';
import { SYNC_STATUS_REFRESH_INTERVAL_MS } from '../../utils/constants';
import { getScheduleSyncStatus } from '../../utils/syncStatus';
import { ScheduleSyncIconContainer, ScheduleSyncStatusContainer } from './ScheduleHeader.styles';
import * as robotScheduleSelectors from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { DATE_FORMAT_PATTERN } from 'lib/utils/date-handling/DateTime.types';

interface ScheduleHeaderSyncStatusProps {
  disabled?: boolean;
}

const ScheduleHeaderSyncStatus = ({ disabled }: ScheduleHeaderSyncStatusProps): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const lastSyncedAt = useSelector(robotScheduleSelectors.selectRobotScheduleOccurrencesLastSyncedAt);
  const isLoading = useSelector(robotScheduleSelectors.selectRobotScheduleOccurrencesIsLoading);
  const isRefreshing = useSelector(robotScheduleSelectors.selectRobotScheduleOccurrencesIsRefreshing);
  const isSyncing = isLoading || isRefreshing;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), SYNC_STATUS_REFRESH_INTERVAL_MS);

    return (): void => window.clearInterval(intervalId);
  }, []);

  const status = getScheduleSyncStatus(lastSyncedAt, now);

  if (!status && !isSyncing) {
    return null;
  }

  const text = ((): string => {
    if (isSyncing || !status) {
      return t('syncStatus.syncing');
    }

    switch (status.kind) {
      case 'upToDate':
        return t('syncStatus.upToDate');
      case 'minutesAgo':
        return t('syncStatus.lastSyncAgo', { minutes: status.minutes });
      default:
        return t('syncStatus.lastSyncDateTime', {
          date: DateTime.formatDateByLocale(i18n.language, status.syncedAt, DATE_FORMAT_PATTERN.DATE_TIME),
        });
    }
  })();

  return (
    <ScheduleSyncStatusContainer
      gap={isSyncing ? kccThemeConstants.SPACING.XS : kccThemeConstants.SPACING.XXS}
      align="center"
      style={{ opacity: disabled ? 0.5 : 1 }}
    >
      <ScheduleSyncIconContainer>
        {isSyncing ? <PendingSpinnerIcon size={kccThemeConstants.SIZE.S200} /> : <SvgIcon name="synchronize" />}
      </ScheduleSyncIconContainer>
      <Typography.Text
        type={disabled ? 'secondary' : undefined}
        style={{ fontSize: kccThemeConstants.TYPOGRAPHY.FONT_SIZE.SM, whiteSpace: 'nowrap' }}
      >
        {text}
      </Typography.Text>
    </ScheduleSyncStatusContainer>
  );
};

export const ScheduleHeaderSyncStatusMemoized = memo(ScheduleHeaderSyncStatus);
